import { APIService } from '../../../lib/api/APIService';

// Website Interview API class extending the base APIService
class WebsiteInterviewAPI extends APIService {
  async getHealth() {
    return this.get('/api/website-interview/health');
  }

  async generateSpeech(text) {
    return this.post('/api/website-interview/generate-speech', { text });
  }

  async transcribeAudioBlob(audioBlob) {
    // Convert Blob to base64 so we can send JSON to the backend
    const arrayBuffer = await audioBlob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64Audio = btoa(binary);

    return this.post('/api/website-interview/transcribe', {
      audio_data: base64Audio,
    });
  }

  async scrapeJob(url) {
    return this.post('/api/website-interview/scrape-job', { url });
  }

  async startInterview(formData) {
    // FormData needs to be sent directly, not as JSON
    const token = this.getAccessToken();
    const url = `${this.baseURL}/api/website-interview/start-interview`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to start interview' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async processSpeech(sessionId, message, sessionData = null, allQuestions = null, currentQuestionNumber = null, role = null) {
    const payload = {
      session_id: sessionId,
      message: message
    };
    
    // The page's role travels at the top level so the server can resolve the
    // package it belongs to when the in-memory session is on another worker.
    if (role) {
      payload.role = role;
    }

    // Standalone mode: send all questions and current question number
    if (allQuestions && allQuestions.length >= 3) {
      payload.all_questions = allQuestions;
      payload.current_question_number = currentQuestionNumber;
    } else if (sessionData) {
      // Fallback: include session_data for session recreation if available
      payload.session_data = sessionData;
    }
    
    return this.post('/api/website-interview/process-speech', payload);
  }

  async captureFrame(sessionId, frameData) {
    return this.post('/api/website-interview/capture-frame', {
      session_id: sessionId,
      frame_data: frameData
    });
  }

  async generateConversationFeedback(sessionId, sessionData = null) {
    const payload = { session_id: sessionId };
    if (sessionData) {
      payload.session_data = sessionData;
    }
    return this.post('/api/website-interview/generate-conversation-feedback', payload);
  }

  async generateVisualFeedback(sessionId, capturedFrames = null) {
    const payload = { session_id: sessionId };
    if (capturedFrames) {
      payload.captured_frames = capturedFrames;
    }
    return this.post('/api/website-interview/generate-visual-feedback', payload);
  }

  async generateFeedback(sessionId, capturedFrames = null) {
    const payload = { session_id: sessionId };
    if (capturedFrames) {
      payload.captured_frames = capturedFrames;
    }
    return this.post('/api/website-interview/generate-feedback', payload);
  }

  async endSession(sessionId) {
    return this.post('/api/website-interview/end-session', {
      session_id: sessionId
    });
  }
}

// Create and export website interview API instance
export const websiteInterviewAPI = new WebsiteInterviewAPI();


